// Lagrangian particle tracking: place up to 10 particles, advect with RK4
// using depth-averaged water velocity + Stokes drift + windage.
// Memory-conscious: only loads leaf tiles near particles, keeps at most two
// vector timesteps resident, and stores compact path samples only.

import {FILL_VALUE} from './tile-store.js';

const TRACK_PASSWORD = 'armycorps';
const MAX_PARTICLES = 10;
const CLICK_SLOP_PX = 6;
/** Classic surface Stokes ≈ 1.6% of 10 m wind. */
const STOKES_FACTOR = 0.016;
/** Peak windage coefficient when fully exposed above water. */
const WINDAGE_SCALE = 0.03;
/** RK4 substeps per model interval (hourly → 15 min). */
const SUBSTEPS_PER_INTERVAL = 4;
const PARTICLE_COLORS = [
  [236, 72, 153, 230],
  [59, 130, 246, 230],
  [16, 185, 129, 230],
  [245, 158, 11, 230],
  [139, 92, 246, 230],
  [239, 68, 68, 230],
  [14, 165, 233, 230],
  [132, 204, 22, 230],
  [249, 115, 22, 230],
  [168, 85, 247, 230]
];

class TrackAbortError extends Error {
  constructor(message = 'Tracking stopped') {
    super(message);
    this.name = 'TrackAbortError';
  }
}

function downloadText(filename, text, mime = 'text/csv;charset=utf-8') {
  const blob = new Blob([text], {type: mime});
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1500);
}

function sanitizeFilename(s) {
  return String(s || 'track')
    .replace(/[^\w.\-]+/g, '_')
    .replace(/_+/g, '_')
    .slice(0, 80);
}

function unwrapLonNear(lon, refLon) {
  let x = lon;
  while (x - refLon > 180) x -= 360;
  while (x - refLon < -180) x += 360;
  return x;
}

function wrapLon(lon) {
  let x = lon;
  while (x > 180) x -= 360;
  while (x < -180) x += 360;
  return x;
}

function clampLat(lat) {
  return Math.max(-89.9, Math.min(89.9, lat));
}

function metersPerDeg(lat) {
  const cos = Math.cos((lat * Math.PI) / 180);
  return {
    lon: Math.max(1e-6, 111320 * Math.max(0.05, Math.abs(cos))),
    lat: 110540
  };
}

function advectLonLat(lon, lat, u, v, dtSec) {
  const m = metersPerDeg(lat);
  return {
    lon: wrapLon(lon + (u * dtSec) / m.lon),
    lat: clampLat(lat + (v * dtSec) / m.lat)
  };
}

function pointInTriangleWeights(px, py, ax, ay, bx, by, cx, cy) {
  const v0x = cx - ax;
  const v0y = cy - ay;
  const v1x = bx - ax;
  const v1y = by - ay;
  const v2x = px - ax;
  const v2y = py - ay;
  const dot00 = v0x * v0x + v0y * v0y;
  const dot01 = v0x * v1x + v0y * v1y;
  const dot02 = v0x * v2x + v0y * v2y;
  const dot11 = v1x * v1x + v1y * v1y;
  const dot12 = v1x * v2x + v1y * v2y;
  const denom = dot00 * dot11 - dot01 * dot01;
  if (Math.abs(denom) < 1e-20) return null;
  const inv = 1 / denom;
  const u = (dot11 * dot02 - dot01 * dot12) * inv;
  const v = (dot00 * dot12 - dot01 * dot02) * inv;
  const w = 1 - u - v;
  if (u < -1e-5 || v < -1e-5 || w < -1e-5) return null;
  return {w0: w, w1: v, w2: u};
}

function parseManifestTimeMs(ts) {
  const s = String(ts).trim().replace(' ', 'T');
  const ms = Date.parse(s.endsWith('Z') ? s : `${s}Z`);
  return Number.isFinite(ms) ? ms : NaN;
}

function formatCsvTime(ts) {
  return String(ts).trim();
}

/** Prefer highest-z leaves whose real bbox contains the point. */
function candidateLeafTiles(index, lon, lat, padDeg = 0.25) {
  if (!index?.leafSet) return [];
  const hits = [];
  for (const key of index.leafSet) {
    const bbox = index.leafBBox.get(key);
    if (!bbox) continue;
    let [w, s, e, n] = bbox;
    // Simple pad; antimeridian-straddling tiles are uncommon for coastal leaves.
    if (lon < w - padDeg || lon > e + padDeg || lat < s - padDeg || lat > n + padDeg) {
      continue;
    }
    const [z, x, y] = key.split(',').map(Number);
    hits.push({z, x, y, key, area: (e - w) * (n - s)});
  }
  hits.sort((a, b) => b.z - a.z || a.area - b.area);
  return hits;
}

function sampleVectorAt(entry, uv, lon, lat, hintTri, {fillAsZero = false} = {}) {
  if (!entry?.positions3 || !entry?.indices || !uv) return null;
  const pos = entry.positions3;
  const idx = entry.indices;
  const nTris = idx.length / 3;

  const tryTri = t => {
    if (t < 0 || t >= nTris) return null;
    const i0 = idx[t * 3];
    const i1 = idx[t * 3 + 1];
    const i2 = idx[t * 3 + 2];
    const ax = unwrapLonNear(pos[i0 * 3], lon);
    const ay = pos[i0 * 3 + 1];
    const bx = unwrapLonNear(pos[i1 * 3], lon);
    const by = pos[i1 * 3 + 1];
    const cx = unwrapLonNear(pos[i2 * 3], lon);
    const cy = pos[i2 * 3 + 1];
    const w = pointInTriangleWeights(lon, lat, ax, ay, bx, by, cx, cy);
    if (!w) return null;

    const read = i => {
      const u = uv[i * 2];
      const v = uv[i * 2 + 1];
      if (u === FILL_VALUE || v === FILL_VALUE || !Number.isFinite(u + v)) {
        return fillAsZero ? {u: 0, v: 0, dry: true} : null;
      }
      return {u, v, dry: false};
    };
    const a = read(i0);
    const b = read(i1);
    const c = read(i2);
    if (!a || !b || !c) return null;
    return {
      u: w.w0 * a.u + w.w1 * b.u + w.w2 * c.u,
      v: w.w0 * a.v + w.w1 * b.v + w.w2 * c.v,
      tri: t,
      dry: a.dry && b.dry && c.dry
    };
  };

  if (hintTri != null) {
    const hit = tryTri(hintTri);
    if (hit) return hit;
  }

  // Cheap AABB reject then full scan (leaf tiles are modest).
  let minLon = Infinity;
  let maxLon = -Infinity;
  let minLat = Infinity;
  let maxLat = -Infinity;
  const nVerts = pos.length / 3;
  for (let i = 0; i < nVerts; i++) {
    const vx = unwrapLonNear(pos[i * 3], lon);
    const vy = pos[i * 3 + 1];
    if (vx < minLon) minLon = vx;
    if (vx > maxLon) maxLon = vx;
    if (vy < minLat) minLat = vy;
    if (vy > maxLat) maxLat = vy;
  }
  if (lon < minLon - 0.5 || lon > maxLon + 0.5 || lat < minLat - 0.5 || lat > maxLat + 0.5) {
    return null;
  }

  for (let t = 0; t < nTris; t++) {
    const hit = tryTri(t);
    if (hit) return hit;
  }
  return null;
}

function releaseVectorTimestep(store, timestep) {
  if (!store?.cache) return;
  for (const entry of store.cache.values()) {
    entry.velocityByTimestep?.delete(timestep);
    entry.windByTimestep?.delete(timestep);
    entry.velocityPromises?.delete(timestep);
    entry.windPromises?.delete(timestep);
    entry.velocitySpeedByTimestep?.delete(timestep);
    entry.windSpeedByTimestep?.delete(timestep);
  }
}

/**
 * @param {object} host callbacks into app.js
 */
export function initTrackParticles(host) {
  const btn = document.getElementById('trackParticlesBtn');
  const modal = document.getElementById('trackModal');
  if (!btn || !modal) return;

  const authGate = document.getElementById('trackAuthGate');
  const modalMain = document.getElementById('trackModalMain');
  const authPassword = document.getElementById('trackAuthPassword');
  const authSubmit = document.getElementById('trackAuthSubmit');
  const authError = document.getElementById('trackAuthError');
  const closeBtn = document.getElementById('trackModalClose');
  const cancelBtn = document.getElementById('trackCancelBtn');
  const placeBtn = document.getElementById('trackPlaceBtn');
  const clearBtn = document.getElementById('trackClearBtn');
  const startBtn = document.getElementById('trackStartBtn');
  const stopBtn = document.getElementById('trackStopBtn');
  const downloadBtn = document.getElementById('trackDownloadBtn');
  const clearPathsBtn = document.getElementById('trackClearPathsBtn');
  const statusEl = document.getElementById('trackStatus');
  const cycleHint = document.getElementById('trackCycleHint');
  const startSel = document.getElementById('trackStartTime');
  const endSel = document.getElementById('trackEndTime');
  const massIn = document.getElementById('trackMass');
  const exposureIn = document.getElementById('trackExposure');
  const countEl = document.getElementById('trackParticleCount');
  const listEl = document.getElementById('trackParticleList');
  const banner = document.getElementById('trackPlaceBanner');
  const bannerText = document.getElementById('trackPlaceBannerText');
  const placeDoneBtn = document.getElementById('trackPlaceDoneBtn');
  // overlay element reserved for future hit-testing chrome; placement uses
  // window pointer listeners so deck.gl can still pan/zoom.
  void document.getElementById('trackPlaceOverlay');

  let authUnlocked = false;
  let authAttemptUsed = false;
  let placing = false;
  let tracking = false;
  let abortRequested = false;
  let particles = []; // {id, lon, lat}
  let nextId = 1;
  let lastResult = null; // {rows, paths, meta}

  function setStatus(msg) {
    if (statusEl) statusEl.textContent = msg || '';
  }

  function updateCount() {
    if (countEl) countEl.textContent = `${particles.length} / ${MAX_PARTICLES}`;
    if (listEl) {
      listEl.innerHTML = '';
      for (const p of particles) {
        const li = document.createElement('li');
        const color = PARTICLE_COLORS[(p.id - 1) % PARTICLE_COLORS.length];
        li.innerHTML =
          `<span class="track-swatch" style="background:rgb(${color[0]},${color[1]},${color[2]})"></span>` +
          `#${p.id}  ${p.lat.toFixed(4)}, ${p.lon.toFixed(4)}`;
        listEl.appendChild(li);
      }
    }
    if (startBtn) startBtn.disabled = particles.length === 0 || tracking;
    if (clearBtn) clearBtn.disabled = particles.length === 0 || tracking || placing;
    host.setTrackMarkers?.(particles.map(p => ({...p})));
  }

  function fillTimeSelects() {
    const timesteps = host.getTimesteps() || [];
    for (const sel of [startSel, endSel]) {
      if (!sel) continue;
      sel.innerHTML = '';
      timesteps.forEach((ts, i) => {
        const opt = document.createElement('option');
        opt.value = String(i);
        opt.textContent = String(ts);
        sel.appendChild(opt);
      });
    }
    if (startSel && timesteps.length) startSel.value = '0';
    if (endSel && timesteps.length) endSel.value = String(timesteps.length - 1);
  }

  function resetAuthGate() {
    authUnlocked = false;
    authAttemptUsed = false;
    authGate?.classList.remove('hidden');
    modalMain?.classList.add('hidden');
    if (authPassword) {
      authPassword.value = '';
      authPassword.disabled = false;
    }
    if (authSubmit) authSubmit.disabled = false;
    if (authError) authError.textContent = '';
  }

  function showMain() {
    authUnlocked = true;
    authGate?.classList.add('hidden');
    modalMain?.classList.remove('hidden');
    fillTimeSelects();
    const cycle = host.getForecastCycle() || '(unknown)';
    const man = host.getManifest();
    const hasV = Boolean(man?.has_velocity);
    const hasW = Boolean(man?.has_wind);
    cycleHint.textContent =
      `Cycle: ${cycle}. Uses water velocity + Stokes drift + windage` +
      (!hasV || !hasW
        ? ` — warning: this cycle may be missing ${!hasV ? 'velocity' : ''}${!hasV && !hasW ? ' and ' : ''}${!hasW ? 'wind' : ''}.`
        : '.');
    setStatus('');
    downloadBtn?.classList.add('hidden');
    clearPathsBtn?.classList.add('hidden');
    stopBtn?.classList.add('hidden');
    updateCount();
  }

  function tryUnlock() {
    if (authUnlocked || authAttemptUsed) return;
    authAttemptUsed = true;
    const entered = (authPassword?.value || '').trim();
    if (entered === TRACK_PASSWORD) {
      if (authError) authError.textContent = '';
      showMain();
      return;
    }
    if (authPassword) {
      authPassword.value = '';
      authPassword.disabled = true;
    }
    if (authSubmit) authSubmit.disabled = true;
    if (authError) {
      authError.textContent =
        'Wrong password. Close this dialog to return to the viewer.';
    }
  }

  function openModal() {
    const man = host.getManifest();
    if (!man) {
      resetAuthGate();
      if (authError) authError.textContent = 'Load a forecast cycle first.';
      if (authPassword) authPassword.disabled = true;
      if (authSubmit) authSubmit.disabled = true;
      authAttemptUsed = true;
      modal.classList.remove('hidden');
      return;
    }
    resetAuthGate();
    modal.classList.remove('hidden');
    authPassword?.focus();
  }

  function stopPlaceMode() {
    placing = false;
    host.setTrackPlaceMode?.(false);
    banner?.classList.remove('visible');
    modal.classList.remove('hidden');
  }

  function closeModal() {
    if (tracking) return;
    stopPlaceMode();
    modal.classList.add('hidden');
    resetAuthGate();
  }

  function startPlaceMode() {
    if (tracking) return;
    placing = true;
    host.setTrackPlaceMode?.(true);
    modal.classList.add('hidden');
    banner?.classList.add('visible');
    if (bannerText) {
      bannerText.textContent =
        `Click to place particles (${particles.length}/${MAX_PARTICLES}). ` +
        'Pan/zoom freely — only solid clicks place a particle. Esc cancels.';
    }
  }

  function placeAtScreen(x, y) {
    if (particles.length >= MAX_PARTICLES) {
      setStatus(`Maximum of ${MAX_PARTICLES} particles reached.`);
      if (bannerText) {
        bannerText.textContent =
          `Maximum of ${MAX_PARTICLES} particles. Click Done placing when ready.`;
      }
      return;
    }
    const deck = host.getDeck?.();
    const vp = deck?.getViewports?.()?.[0];
    if (!vp) return;
    const [lon, lat] = vp.unproject([x, y]);
    if (!Number.isFinite(lon) || !Number.isFinite(lat)) return;
    particles.push({id: nextId++, lon: wrapLon(lon), lat: clampLat(lat)});
    updateCount();
    if (bannerText) {
      bannerText.textContent =
        particles.length >= MAX_PARTICLES
          ? `Maximum of ${MAX_PARTICLES} particles. Click Done placing when ready.`
          : `Click to place particles (${particles.length}/${MAX_PARTICLES}). ` +
            'Pan/zoom freely — only solid clicks place a particle. Esc cancels.';
    }
  }

  function onKeyDown(e) {
    if (e.key === 'Escape' && placing) {
      e.preventDefault();
      stopPlaceMode();
      setStatus('Placement cancelled.');
    }
  }

  function readParams() {
    const timesteps = host.getTimesteps() || [];
    let i0 = parseInt(startSel?.value ?? '0', 10);
    let i1 = parseInt(endSel?.value ?? String(timesteps.length - 1), 10);
    if (!Number.isFinite(i0)) i0 = 0;
    if (!Number.isFinite(i1)) i1 = timesteps.length - 1;
    i0 = Math.max(0, Math.min(timesteps.length - 1, i0));
    i1 = Math.max(0, Math.min(timesteps.length - 1, i1));
    if (i1 < i0) [i0, i1] = [i1, i0];

    let mass = parseFloat(massIn?.value);
    if (!Number.isFinite(mass) || mass < 0) mass = 1;
    let exposure = parseFloat(exposureIn?.value);
    if (!Number.isFinite(exposure)) exposure = 0.2;
    exposure = Math.max(0, Math.min(1, exposure));

    return {i0, i1, mass, exposure, timesteps};
  }

  async function ensureTileVectors(store, z, x, y, timestep) {
    await store.ensureGeometry(z, x, y);
    await store.ensureVector(z, x, y, timestep, 'velocity');
    await store.ensureVector(z, x, y, timestep, 'wind');
  }

  /**
   * Sample combined drift velocity (m/s) at (lon,lat) for a fractional time
   * between timestep indices tA and tB (alpha in [0,1]).
   */
  async function sampleDrift(store, index, lon, lat, tA, tB, alpha, exposure, mass, hints) {
    // On wet mesh: V = V_water + (Stokes + windage)·U10.
    // On dry land: V_water is treated as 0 (no bounce/stop); wind still conveys
    // the particle. Stokes is wave-driven, so on fully dry cells only windage
    // is applied.
    const windageCoef = (exposure * WINDAGE_SCALE) / (1 + Math.max(0, mass) / 50);
    const tiles = candidateLeafTiles(index, lon, lat);
    if (!tiles.length) return null;

    for (const tile of tiles.slice(0, 6)) {
      const {z, x, y} = tile;
      try {
        await ensureTileVectors(store, z, x, y, tA);
        if (tB !== tA) await ensureTileVectors(store, z, x, y, tB);
      } catch (_) {
        continue;
      }
      const entry = store.get(z, x, y);
      if (!entry) continue;
      const uvA = entry.velocityByTimestep?.get(tA);
      const windA = entry.windByTimestep?.get(tA);
      if (!windA) continue;
      const hintKey = `${z},${x},${y}`;
      const hint = hints.get(hintKey);

      // Wind is defined on land; velocity dry nodes → 0 (convey with wind).
      const wndA = sampleVectorAt(entry, windA, lon, lat, hint?.tri);
      if (!wndA) continue;
      const velA = uvA
        ? sampleVectorAt(entry, uvA, lon, lat, wndA.tri, {fillAsZero: true})
        : {u: 0, v: 0, dry: true, tri: wndA.tri};

      const dryA = !velA || velA.dry;
      const waterUA = velA ? velA.u : 0;
      const waterVA = velA ? velA.v : 0;
      const windCoefA = dryA ? windageCoef : STOKES_FACTOR + windageCoef;
      let u = waterUA + windCoefA * wndA.u;
      let v = waterVA + windCoefA * wndA.v;

      if (tB !== tA && alpha > 0) {
        const uvB = entry.velocityByTimestep?.get(tB);
        const windB = entry.windByTimestep?.get(tB);
        if (windB) {
          const wndB = sampleVectorAt(entry, windB, lon, lat, wndA.tri);
          if (wndB) {
            const velB = uvB
              ? sampleVectorAt(entry, uvB, lon, lat, wndB.tri, {fillAsZero: true})
              : {u: 0, v: 0, dry: true, tri: wndB.tri};
            const dryB = !velB || velB.dry;
            const windCoefB = dryB ? windageCoef : STOKES_FACTOR + windageCoef;
            const u1 = (velB ? velB.u : 0) + windCoefB * wndB.u;
            const v1 = (velB ? velB.v : 0) + windCoefB * wndB.v;
            u = u * (1 - alpha) + u1 * alpha;
            v = v * (1 - alpha) + v1 * alpha;
          }
        }
      }

      hints.set(hintKey, {tri: wndA.tri});
      return {u, v, z, dry: dryA};
    }
    return null;
  }

  async function runTracking() {
    if (tracking || !particles.length) return;
    const {i0, i1, mass, exposure, timesteps} = readParams();
    if (i1 <= i0) {
      setStatus('End time must be after start time.');
      return;
    }
    const store = host.getStore?.();
    const index = host.getIndex?.();
    if (!store || !index) {
      setStatus('Tile store not ready.');
      return;
    }

    tracking = true;
    abortRequested = false;
    startBtn.disabled = true;
    placeBtn.disabled = true;
    clearBtn.disabled = true;
    stopBtn?.classList.remove('hidden');
    downloadBtn?.classList.add('hidden');
    clearPathsBtn?.classList.add('hidden');
    btn.disabled = true;
    host.setTrackResult?.(null);

    const states = particles.map(p => ({
      id: p.id,
      lon: p.lon,
      lat: p.lat,
      alive: true,
      lastU: 0,
      lastV: 0,
      hints: new Map()
    }));
    const paths = new Map(states.map(s => [s.id, [{t: i0, lon: s.lon, lat: s.lat}]]));
    const rows = [];
    for (const s of states) {
      rows.push({
        particle_id: s.id,
        datetime: formatCsvTime(timesteps[i0]),
        lon: s.lon,
        lat: s.lat
      });
    }

    // Wet: V = V_water + Stokes + windage. Dry land: V_water→0, windage still
    // conveys (no stop / no bounce). Brief off-mesh gaps coast on last drift.

    let prevKeep = i0;
    try {
      for (let ti = i0; ti < i1; ti++) {
        if (abortRequested) throw new TrackAbortError();
        const t0ms = parseManifestTimeMs(timesteps[ti]);
        const t1ms = parseManifestTimeMs(timesteps[ti + 1]);
        let dtTotal = (t1ms - t0ms) / 1000;
        if (!Number.isFinite(dtTotal) || dtTotal <= 0) dtTotal = 3600;

        host.setTimestep?.(ti);
        setStatus(
          `Tracking… interval ${ti - i0 + 1}/${i1 - i0} ` +
            `(${timesteps[ti]} → ${timesteps[ti + 1]})`
        );

        const nSub = SUBSTEPS_PER_INTERVAL;
        const dtSub = dtTotal / nSub;

        for (let s = 0; s < nSub; s++) {
          if (abortRequested) throw new TrackAbortError();
          const alpha0 = s / nSub;
          const alpha1 = (s + 1) / nSub;

          for (const st of states) {
            const driftOrCoast = async (lon, lat, alpha) => {
              const sampled = await sampleDrift(
                store,
                index,
                lon,
                lat,
                ti,
                ti + 1,
                alpha,
                exposure,
                mass,
                st.hints
              );
              if (sampled) {
                st.lastU = sampled.u;
                st.lastV = sampled.v;
                return sampled;
              }
              // Off-mesh gap: keep conveying with last known drift (no stop).
              return {u: st.lastU, v: st.lastV};
            };

            const k1 = await driftOrCoast(st.lon, st.lat, alpha0);
            const p2 = advectLonLat(st.lon, st.lat, k1.u, k1.v, dtSub / 2);
            const k2 = await driftOrCoast(
              p2.lon,
              p2.lat,
              alpha0 + 0.5 * (alpha1 - alpha0)
            );
            const p3 = advectLonLat(st.lon, st.lat, k2.u, k2.v, dtSub / 2);
            const k3 = await driftOrCoast(
              p3.lon,
              p3.lat,
              alpha0 + 0.5 * (alpha1 - alpha0)
            );
            const p4 = advectLonLat(st.lon, st.lat, k3.u, k3.v, dtSub);
            const k4 = await driftOrCoast(p4.lon, p4.lat, alpha1);
            const u = (k1.u + 2 * k2.u + 2 * k3.u + k4.u) / 6;
            const v = (k1.v + 2 * k2.v + 2 * k3.v + k4.v) / 6;
            st.lastU = u;
            st.lastV = v;
            const next = advectLonLat(st.lon, st.lat, u, v, dtSub);
            st.lon = next.lon;
            st.lat = next.lat;
          }

          // Yield so UI stays responsive without loading everything at once.
          await new Promise(r => setTimeout(r, 0));
        }

        for (const st of states) {
          paths.get(st.id).push({t: ti + 1, lon: st.lon, lat: st.lat});
          rows.push({
            particle_id: st.id,
            datetime: formatCsvTime(timesteps[ti + 1]),
            lon: st.lon,
            lat: st.lat
          });
        }

        // Drop older vector timestep caches (keep recent for reuse).
        if (ti > prevKeep + 1) {
          releaseVectorTimestep(store, prevKeep);
          prevKeep = ti;
        }

        host.setTrackMarkers?.(
          states.map(s => ({id: s.id, lon: s.lon, lat: s.lat}))
        );
      }

      const pathLayerData = [];
      for (const [id, pts] of paths) {
        if (pts.length < 2) continue;
        const color = PARTICLE_COLORS[(id - 1) % PARTICLE_COLORS.length];
        pathLayerData.push({
          id,
          path: pts.map(p => [p.lon, p.lat]),
          color
        });
      }

      lastResult = {
        rows,
        paths: pathLayerData,
        meta: {
          mass,
          exposure,
          stokes: STOKES_FACTOR,
          windageScale: WINDAGE_SCALE,
          cycle: host.getForecastCycle() || '',
          start: timesteps[i0],
          end: timesteps[i1]
        }
      };
      host.setTrackResult?.(lastResult);
      host.setTrackMarkers?.(
        states.map(s => ({id: s.id, lon: s.lon, lat: s.lat}))
      );

      setStatus(
        `Done. Paths are shown on the map — download CSV below. ` +
          'Dry-land segments continue under windage (no stop / no bounce).'
      );
      downloadBtn?.classList.remove('hidden');
      clearPathsBtn?.classList.remove('hidden');
    } catch (err) {
      if (err instanceof TrackAbortError) {
        setStatus('Tracking stopped.');
      } else {
        console.error(err);
        setStatus(`Error: ${err.message || err}`);
      }
    } finally {
      // Free vector timestep caches from this run (geometry stays).
      for (let t = i0; t <= i1; t++) releaseVectorTimestep(store, t);
      tracking = false;
      abortRequested = false;
      startBtn.disabled = particles.length === 0;
      placeBtn.disabled = false;
      clearBtn.disabled = particles.length === 0;
      stopBtn?.classList.add('hidden');
      btn.disabled = false;
    }
  }

  function downloadCsv() {
    if (!lastResult?.rows?.length) return;
    const meta = lastResult.meta || {};
    const header = 'particle_id,datetime,lon,lat\n';
    const body = lastResult.rows
      .map(
        r =>
          `${r.particle_id},${r.datetime},${r.lon.toFixed(6)},${r.lat.toFixed(6)}`
      )
      .join('\n');
    const comment =
      `# cycle=${meta.cycle} mass_kg=${meta.mass} exposure=${meta.exposure}` +
      ` stokes=${meta.stokes} windage_scale=${meta.windageScale}` +
      ` start=${meta.start} end=${meta.end}\n`;
    downloadText(
      sanitizeFilename(`particle_tracks_${meta.cycle || 'cycle'}.csv`),
      comment + header + body + '\n'
    );
  }

  function clearPaths() {
    lastResult = null;
    host.setTrackResult?.(null);
    host.setTrackMarkers?.(particles.map(p => ({...p})));
    downloadBtn?.classList.add('hidden');
    clearPathsBtn?.classList.add('hidden');
    setStatus('Cleared track paths.');
  }

  authSubmit?.addEventListener('click', tryUnlock);
  authPassword?.addEventListener('keydown', e => {
    if (e.key === 'Enter') {
      e.preventDefault();
      tryUnlock();
    }
  });
  btn.addEventListener('click', openModal);
  closeBtn?.addEventListener('click', closeModal);
  cancelBtn?.addEventListener('click', closeModal);
  placeBtn?.addEventListener('click', startPlaceMode);
  placeDoneBtn?.addEventListener('click', () => {
    stopPlaceMode();
    setStatus(
      particles.length
        ? `${particles.length} particle(s) placed. Start tracking when ready.`
        : 'No particles placed.'
    );
  });
  clearBtn?.addEventListener('click', () => {
    particles = [];
    nextId = 1;
    updateCount();
    setStatus('Cleared particles.');
  });
  startBtn?.addEventListener('click', () => {
    runTracking().catch(err => {
      console.error(err);
      setStatus(`Error: ${err.message || err}`);
    });
  });
  stopBtn?.addEventListener('click', () => {
    if (tracking) {
      abortRequested = true;
      setStatus('Stopping…');
    }
  });
  downloadBtn?.addEventListener('click', downloadCsv);
  clearPathsBtn?.addEventListener('click', clearPaths);
  window.addEventListener('keydown', onKeyDown);

  // Window-level solid-click detection so pan/zoom still reaches deck.gl.
  let placePtr = null;
  function onWinPointerDown(e) {
    if (!placing) return;
    if (e.target.closest?.('#trackPlaceBanner, #trackModal')) return;
    placePtr = {x: e.clientX, y: e.clientY, moved: false};
  }
  function onWinPointerMove(e) {
    if (!placePtr) return;
    if (Math.hypot(e.clientX - placePtr.x, e.clientY - placePtr.y) > CLICK_SLOP_PX) {
      placePtr.moved = true;
    }
  }
  function onWinPointerUp(e) {
    if (!placing || !placePtr) return;
    const wasClick = !placePtr.moved;
    const x = placePtr.x;
    const y = placePtr.y;
    placePtr = null;
    if (wasClick && !e.target.closest?.('#trackPlaceBanner, #trackModal')) {
      placeAtScreen(x, y);
    }
  }
  window.addEventListener('pointerdown', onWinPointerDown, true);
  window.addEventListener('pointermove', onWinPointerMove, true);
  window.addEventListener('pointerup', onWinPointerUp, true);
}
